/**
 * TanStack Query hooks for the labeling factory API
 * (`/api/labeling/factory/...` in web-demo-backend).
 *
 * The factory replaces hand-run worklist-generation scripts with a
 * self-serve pool/phase-state engine: annotators accept a project, work
 * through demo/working phases requesting more batches as they finish, and
 * admins register projects, ingest pool cases, and approve phase advances.
 * See PLANS/specs/2026-07-03-annotation-factory-design.md and
 * PLANS/plans/2026-07-03-annotation-factory-stage-a.md for the backend
 * contract these hooks target.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { useLabelingRole } from "./useLabelingRole";
import type {
  Project,
  Enrollment,
  Batch,
  PoolStatus,
  AvailableProject,
  RequestMoreResult,
  MyEnrollment,
  PortfolioAnalytics,
  ProjectAnalytics,
} from "../types/factory";

function factoryBase(): string {
  return `${getApiBaseUrl()}/api/labeling/factory`;
}

// ============================================================================
// Query Keys
// ============================================================================

export const factoryKeys = {
  projects: ["factory", "projects"] as const,
  myEnrollments: ["factory", "my-enrollments"] as const,
  availableProjects: ["factory", "available-projects"] as const,
  enrollments: (pid: string) => ["factory", "enrollments", pid] as const,
  poolStatus: (pid: string) => ["factory", "pool-status", pid] as const,
  analytics: (pid: string) => ["factory", "analytics", pid] as const,
  portfolioAnalytics: ["factory", "analytics", "portfolio"] as const,
};

// ============================================================================
// Error helpers
// ============================================================================

/** Error thrown by mutations below when the backend responds 409 (pool
 *  exhausted). Callers branch on `.poolExhausted` rather than parsing
 *  status codes/messages themselves. */
export type FactoryError = Error & { poolExhausted?: true };

/** Re-throws 409s as a `FactoryError` with `poolExhausted: true`; anything
 *  else is re-thrown unchanged. Always throws — never returns. */
function rethrowPoolExhausted(err: unknown): never {
  const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response
    ?.status;
  if (status === 409) {
    const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    const poolError: FactoryError = new Error(detail ?? "Pool exhausted");
    poolError.poolExhausted = true;
    throw poolError;
  }
  throw err;
}

// ============================================================================
// Query Hooks
// ============================================================================

/** Registered projects the caller could accept (not yet enrolled in). */
export function useAvailableProjectsQuery(): UseQueryResult<AvailableProject[]> {
  return useQuery({
    queryKey: factoryKeys.availableProjects,
    queryFn: async () => {
      const response = await axios.get<AvailableProject[]>(`${factoryBase()}/available-projects`);
      return response.data;
    },
    retry: 1,
  });
}

/**
 * The caller's own enrollments, enriched with project/worklist info.
 *
 * A missing route is a deployment regression, not an empty enrollment list,
 * so query errors deliberately remain visible to the caller.
 */
export function useMyEnrollmentsQuery(): UseQueryResult<MyEnrollment[]> {
  return useQuery({
    queryKey: factoryKeys.myEnrollments,
    queryFn: async () => (await axios.get<MyEnrollment[]>(`${factoryBase()}/my-enrollments`)).data,
    retry: 1,
  });
}

/**
 * Registered projects visible to an admin, scoped manager, or internal reader.
 *
 * Plain annotators never see
 * 403 noise from a route they have no business hitting. Internal is
 * included (not just admin) so `InternalCategory`'s read-only view actually
 * fires this query — an admin-only gate here left that tier permanently
 * `fetchStatus: "idle"`.
 */
export function useFactoryProjectsQuery(): UseQueryResult<Project[]> {
  const role = useLabelingRole();
  return useQuery({
    queryKey: factoryKeys.projects,
    queryFn: async () => (await axios.get<Project[]>(`${factoryBase()}/projects`)).data,
    enabled: role === "admin" || role === "manager" || role === "internal",
    retry: 1,
  });
}

/** Enrollments for a single project (admin view). */
export function useProjectEnrollmentsQuery(pid: string): UseQueryResult<Enrollment[]> {
  return useQuery({
    queryKey: factoryKeys.enrollments(pid),
    queryFn: async () => {
      const response = await axios.get<Enrollment[]>(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/enrollments`,
      );
      return response.data;
    },
    enabled: !!pid,
    retry: 2,
  });
}

/** Pool counts (available / awaiting_second / gt) for a project (admin view). */
export function usePoolStatusQuery(pid: string): UseQueryResult<PoolStatus> {
  return useQuery({
    queryKey: factoryKeys.poolStatus(pid),
    queryFn: async () => {
      const response = await axios.get<PoolStatus>(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/pool-status`,
      );
      return response.data;
    },
    enabled: !!pid,
    retry: 2,
  });
}

/** Aggregated completion, timing, and evaluation detail for one project. */
export function useProjectAnalyticsQuery(
  pid: string,
  startDate?: string,
  endDate?: string,
): UseQueryResult<ProjectAnalytics> {
  const role = useLabelingRole();
  return useQuery({
    queryKey: [...factoryKeys.analytics(pid), startDate, endDate],
    queryFn: async () => {
      const params = {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      };
      const response = await axios.get<ProjectAnalytics>(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/analytics`,
        Object.keys(params).length > 0 ? { params } : undefined,
      );
      return response.data;
    },
    enabled: role === "admin" && !!pid,
    staleTime: 60_000,
    retry: 1,
  });
}

/** Aggregated completion, timing, and evaluation detail across all projects. */
export function usePortfolioAnalyticsQuery(
  startDate?: string,
  endDate?: string,
): UseQueryResult<PortfolioAnalytics> {
  const role = useLabelingRole();
  return useQuery({
    queryKey: [...factoryKeys.portfolioAnalytics, startDate, endDate],
    queryFn: async () => {
      const response = await axios.get<PortfolioAnalytics>(`${factoryBase()}/analytics`, {
        params: {
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        },
      });
      return response.data;
    },
    enabled: role === "admin",
    staleTime: 60_000,
    retry: 1,
  });
}

// ============================================================================
// Mutation Hooks — annotator self-serve
// ============================================================================

/**
 * Self-serve accept into the demo phase of a project (POST .../accept).
 * On success, the accepted project drops out of "available" and shows up
 * under "my enrollments".
 */
export function useAcceptProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pid: string) => {
      try {
        const response = await axios.post<Batch>(
          `${factoryBase()}/projects/${encodeURIComponent(pid)}/accept`,
        );
        return response.data;
      } catch (err) {
        rethrowPoolExhausted(err);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.availableProjects });
      queryClient.invalidateQueries({ queryKey: factoryKeys.myEnrollments });
    },
  });
}

/**
 * Request the next batch once the current one is finished (POST
 * .../request-more). Backend responds `assigned` (next batch), or
 * `pending_approval` if the phase gate requires an admin approve first.
 */
export function useRequestMoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pid: string) => {
      try {
        const response = await axios.post<RequestMoreResult>(
          `${factoryBase()}/projects/${encodeURIComponent(pid)}/request-more`,
        );
        return response.data;
      } catch (err) {
        rethrowPoolExhausted(err);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.myEnrollments });
    },
  });
}

// ============================================================================
// Mutation Hooks — admin
// ============================================================================

export interface ApproveEnrollmentInput {
  pid: string;
  annotator: string;
}

/** Advance a `pending_approval` enrollment to the next phase (admin). */
export function useApproveEnrollmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, annotator }: ApproveEnrollmentInput) => {
      try {
        const response = await axios.post<Enrollment>(
          `${factoryBase()}/enrollments/${encodeURIComponent(pid)}/${encodeURIComponent(annotator)}/approve`,
        );
        return response.data;
      } catch (err) {
        rethrowPoolExhausted(err);
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.enrollments(variables.pid) });
    },
  });
}

export interface AgreementCheckInput {
  pid: string;
  annotator: string;
  batchId?: string;
}

/** Generous: the recompute is S3-bound and scales with the shared-case count.
 *  Long enough not to cut off a legitimately slow check, short enough that a
 *  stalled one still reaches a terminal state. */
const AGREEMENT_CHECK_TIMEOUT_MS = 120_000;

/** Read-only, on-demand agreement recompute for ONE annotator's current
 *  batch (admin). Returns the live scorecard dict; the backend performs no
 *  writes (no finalize/email/advance). Results are displayed ephemerally by
 *  the caller — no query cache to invalidate. */
export function useAgreementCheckMutation() {
  return useMutation({
    mutationFn: async ({ pid, annotator, batchId }: AgreementCheckInput) => {
      const response = await axios.post<Record<string, unknown>>(
        `${factoryBase()}/enrollments/${encodeURIComponent(pid)}/${encodeURIComponent(annotator)}/agreement-check`,
        undefined,
        // The backend re-runs evaluate_batch: an S3 mask read + resample per
        // shared case, uncached. Without a timeout a stalled request leaves
        // the row spinning and the button disabled forever, with no terminal
        // state to recover from.
        {
          timeout: AGREEMENT_CHECK_TIMEOUT_MS,
          params: batchId ? { batch_id: batchId } : undefined,
        },
      );
      return response.data;
    },
  });
}

/** Register a new project + phase config (admin). */
export function useRegisterProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project: Project) => {
      const response = await axios.post<Project>(`${factoryBase()}/projects`, project);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
    },
  });
}

export interface IngestPoolInput {
  pid: string;
  rows: Array<Record<string, unknown>>;
  gt_uids?: string[];
}

export interface IngestCohortPoolInput {
  pid: string;
  cohort_id: string;
  study_uids?: string[];
  gt_uids?: string[];
}

/** Ingest cohort rows into a project's claimable pool (admin). */
export function useIngestPoolMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, rows, gt_uids = [] }: IngestPoolInput) => {
      const response = await axios.post(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/pool`,
        {
          rows,
          gt_uids,
        },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.poolStatus(variables.pid) });
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
    },
  });
}

/** Pull completed cases and model geometry directly from a reader cohort. */
export function useIngestCohortPoolMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pid,
      cohort_id,
      study_uids = [],
      gt_uids = [],
    }: IngestCohortPoolInput) => {
      const response = await axios.post(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/cohort-pool`,
        { cohort_id, study_uids, gt_uids },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.poolStatus(variables.pid) });
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
    },
  });
}

export interface SetGtAnnotatorInput {
  pid: string;
  gt_annotator: string;
}

/** Set (or change) a project's ground-truth annotator (admin). */
export function useSetGtAnnotatorMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, gt_annotator }: SetGtAnnotatorInput) => {
      const response = await axios.put(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/gt-annotator`,
        { gt_annotator },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
    },
  });
}

export interface SetGtReferenceInput {
  pid: string;
  worklist_project_id: string;
}

/** Set the worklist whose masks are used as the project's GT reference. */
export function useSetGtReferenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, worklist_project_id }: SetGtReferenceInput) => {
      const response = await axios.put<Project>(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/gt-reference`,
        { worklist_project_id },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
      queryClient.invalidateQueries({ queryKey: factoryKeys.analytics(variables.pid) });
      queryClient.invalidateQueries({ queryKey: factoryKeys.portfolioAnalytics });
    },
  });
}

export interface SetEmailRecipientsInput {
  pid: string;
  recipients: string[];
}

/** Set a project's scorecard email recipients (admin). */
export function useSetEmailRecipientsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, recipients }: SetEmailRecipientsInput) => {
      const response = await axios.put(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/email-recipients`,
        { recipients },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factoryKeys.projects });
    },
  });
}

export interface SetProjectHiddenInput {
  pid: string;
  hidden: boolean;
}

/**
 * Change whether an unenrolled annotator can discover and accept a project.
 * Hidden projects remain in the admin project list, so the setting is always
 * reversible from the control room.
 */
export function useSetProjectHiddenMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pid, hidden }: SetProjectHiddenInput) => {
      const response = await axios.put<Project>(
        `${factoryBase()}/projects/${encodeURIComponent(pid)}/hidden`,
        { hidden },
      );
      return response.data;
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: factoryKeys.projects }),
        queryClient.invalidateQueries({ queryKey: factoryKeys.availableProjects }),
      ]),
  });
}
